import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useCookieConsentOptional } from "@/hooks/useCookieConsent";
import { TC_FORMAT, storePending, loadPending, clearPending } from "@/lib/pendingClickToken";


/* ── cookie helpers ── */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}
function setCookie(name: string, value: string, days: number) {
  const d = new Date();
  d.setTime(d.getTime() + days * 86400000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/;SameSite=Lax`;
}

function generateId(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/* ── consent helpers ── */
function hasFunctionalConsent(): boolean {
  try {
    const raw = localStorage.getItem("cookie_consent");
    if (!raw) return false;
    const prefs = JSON.parse(raw);
    return prefs.functional === true;
  } catch {
    return false;
  }
}

function enableFunctionalConsent(): void {
  try {
    const raw = localStorage.getItem("cookie_consent");
    const prefs = raw ? JSON.parse(raw) : { functional: false, analytics: false };
    prefs.functional = true;
    localStorage.setItem("cookie_consent", JSON.stringify(prefs));
  } catch { /* ignore */ }
}

/* ── profile persistence via localStorage ── */
const PROFILE_STORAGE_KEY = "tango_visitor_profile";

export interface VisitorProfile {
  name?: string;
  email?: string;
  company?: string;
  phone?: string;
  job_title?: string;
  industry?: string;
  website?: string;
  primary_challenge?: string;
}

function loadStoredProfile(): VisitorProfile {
  try {
    const raw = localStorage.getItem(PROFILE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveStoredProfile(profile: VisitorProfile) {
  try {
    localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile));
  } catch { /* storage full — ignore */ }
}

/* ── fire-and-forget tracker ── */
const EDGE_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-visitor`;
const PERSONALISE_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/personalise-visit`;

async function sendEvent(payload: Record<string, unknown>) {
  try {
    const resp = await fetch(EDGE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!resp.ok) {
      console.warn("[Visitor] track-visitor rejected event:", resp.status, payload.event_type);
    }
  } catch (err) {
    // fire-and-forget — never block the UI, but make the failure visible
    console.warn("[Visitor] track-visitor request failed:", err);
  }
}


/* ── personalisation types ── */
export interface PersonalisationDecisions {
  handoff_message?: string;
  pages?: {
    homepage?: {
      hero_headline?: string;
      hero_description?: string;
      featured_solutions?: string[];
      cta_primary?: { label: string; url: string };
    };
    solutions?: {
      highlight_pillar?: string;
      card_order?: string[];
      industry_callout?: string;
    };
    resources?: {
      recommended_ids?: string[];
      filter_category?: string;
    };
    partners?: {
      filter_regions?: string[];
      filter_platforms?: string[];
    };
    pillar_pages?: Record<string, {
      hero_headline?: string;
      hero_description?: string;
      industry_angle?: string;
    }>;
  };
  proactive_prompts?: string[];
}

export interface PersonalisationResult {
  ok: boolean;
  reason?: string;
  data?: PersonalisationDecisions;
}

/* ── exported context type ── */
export interface VisitorContext {
  visitorCookieId: string | null;
  landingPage: string | null;
  utmSource: string | null;
  utmCampaign: string | null;
  utmMedium: string | null;
  pagesViewed: string[];
  persona: string | null;
  setPersona: (persona: string | null) => void;
  trackEvent: (type: string, metadata?: Record<string, unknown>) => void;
  updateProfile: (fields: Record<string, string>) => void;
  /** Stored visitor profile for auto-populating forms */
  profile: VisitorProfile;
  personalisation: PersonalisationDecisions | null;
  personalisationLoading: boolean;
  triggerPersonalisation: (visitor: Record<string, unknown>, enrichment?: Record<string, unknown>) => Promise<PersonalisationResult>;
  /** Dynamic Mode — session-scoped, re-asked each visit */
  dynamicMode: boolean;
  setDynamicMode: (v: boolean) => void;
  /** Ensure a visitor session exists — auto-enables consent if needed */
  ensureVisitorSession: () => Promise<string | null>;
  /** Pipeline status for the personalisation overlay */
  pipelineStatus: string | null;
  setPipelineStatus: (status: string | null) => void;
  /** An email-click token is waiting for the visitor's choice (item 99). */
  hasPendingTc: boolean;
  /** Throw the pending email-click token away (visitor said No). */
  discardPendingTc: () => void;
}

const COOKIE_NAME = "tango_visitor_id";
const COOKIE_DAYS = 365;
const PERSONALISATION_CACHE_KEY = "tango_personalisation";

export function useVisitorTracking(): VisitorContext {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Consent as React state: when the visitor accepts cookies mid-session this
  // flips false → true and re-arms tracking immediately, without a reload.
  // The localStorage read is the initial value so returning visitors are
  // tracked from first paint.
  const consent = useCookieConsentOptional();
  const consentGranted = consent ? consent.functional : hasFunctionalConsent();
  const [cookieReady, setCookieReady] = useState<boolean>(() => !!getCookie(COOKIE_NAME));

  const pagesViewed = useRef<string[]>([]);
  const visitorId = useRef<string | null>(null);
  const landingPage = useRef<string | null>(null);
  const sentPages = useRef<Set<string>>(new Set());
  // Identified-click token from a marketing email link (?tc=). Read once on
  // load and stripped from the address bar straight away so it is not shared
  // or bookmarked. Held in memory, plus this tab's sessionStorage
  // (tango_pending_tc, 30-minute limit) so a reload before the visitor
  // decides does not lose it. Sent only with the first page_view after
  // functional consent; discarded on "No".
  const pendingTc = useRef<string | null>(null);
  const tcRead = useRef(false);
  if (!tcRead.current) {
    tcRead.current = true;
    try {
      const url = new URL(window.location.href);
      const tc = url.searchParams.get("tc");
      if (tc) {
        if (TC_FORMAT.test(tc)) {
          pendingTc.current = tc;
          storePending(tc);
        }
        url.searchParams.delete("tc");
        window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
      } else {
        pendingTc.current = loadPending();
      }
    } catch { /* ignore */ }
  }
  const [hasPendingTc, setHasPendingTc] = useState<boolean>(() => !!pendingTc.current);
  const takeTc = () => {
    const t = pendingTc.current;
    pendingTc.current = null;
    if (t) { clearPending(); setHasPendingTc(false); }
    return t ?? undefined;
  };
  const discardPendingTc = useCallback(() => {
    pendingTc.current = null;
    clearPending();
    setHasPendingTc(false);
  }, []);
  // A stored "functional off" choice means the token can never be used.
  useEffect(() => {
    if (consent?.hasConsented && !consent.functional && pendingTc.current) discardPendingTc();
  }, [consent?.hasConsented, consent?.functional, discardPendingTc]);
  const [persona, setPersonaState] = useState<string | null>(() => {
    try { return getCookie("tango_persona") || null; } catch { return null; }
  });

  // Visitor profile — initialise from localStorage
  const [profile, setProfile] = useState<VisitorProfile>(loadStoredProfile);

  // Pipeline status for personalisation progress overlay
  const [pipelineStatus, setPipelineStatus] = useState<string | null>(null);

  // Dynamic Mode — session-scoped (re-asked each visit)
  const [dynamicMode, setDynamicModeState] = useState<boolean>(() => {
    try { return sessionStorage.getItem("tango_dynamic_mode") === "true"; } catch { return false; }
  });
  const setDynamicMode = useCallback((v: boolean) => {
    setDynamicModeState(v);
    try { sessionStorage.setItem("tango_dynamic_mode", String(v)); } catch { /* ignore */ }
    // When switching OFF, tear down any cached personalisation so the
    // standard site is restored immediately — without this, hero/CTA
    // overrides that were already swapped in this session linger.
    if (!v) {
      setPersonalisation(null);
      setPipelineStatus(null);
      try { sessionStorage.removeItem(PERSONALISATION_CACHE_KEY); } catch { /* ignore */ }
    }
  }, []);

  // Personalisation state — initialise from sessionStorage cache
  const [personalisation, setPersonalisation] = useState<PersonalisationDecisions | null>(() => {
    try {
      const cached = sessionStorage.getItem(PERSONALISATION_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        console.log("[Personalisation] Restored from cache:", Object.keys(parsed.pages || {}));
        return parsed;
      }
      console.log("[Personalisation] No cache found in sessionStorage");
      return null;
    } catch { return null; }
  });
  const [personalisationLoading, setPersonalisationLoading] = useState(false);

  const setPersona = useCallback((p: string | null) => {
    setPersonaState(p);
    if (p) {
      setCookie("tango_persona", p, COOKIE_DAYS);
    } else {
      document.cookie = "tango_persona=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax";
    }
    const id = visitorId.current || getCookie(COOKIE_NAME);
    if (id && hasFunctionalConsent()) {
      sendEvent({ visitor_cookie_id: id, event_type: "persona_selected", metadata: { persona: p }, persona: p });
    }
  }, []);

  // UTM params (stable for session)
  const utmSource = searchParams.get("utm_source") || searchParams.get("source") || null;
  const utmCampaign = searchParams.get("utm_campaign") || searchParams.get("campaign") || null;
  const utmMedium = searchParams.get("utm_medium") || null;
  // Set by tracked LinkedIn links: the post id, so a session can be traced to a post.
  const utmContent = searchParams.get("utm_content") || null;

  // Initialise cookie — re-runs when consent is granted mid-session so that
  // accepting cookies arms tracking straight away rather than at next reload.
  useEffect(() => {
    if (!consentGranted) return;
    let id = getCookie(COOKIE_NAME);
    const isNew = !id;
    if (!id) {
      id = generateId();
      setCookie(COOKIE_NAME, id, COOKIE_DAYS);
    }
    visitorId.current = id;
    const path = window.location.pathname;
    if (!landingPage.current) landingPage.current = path;
    setCookieReady(true);

    // The page they accepted on is their landing page — send it now, otherwise
    // it is lost and landing_page records wherever they happen to reload.
    if (isNew && !sentPages.current.has(path)) {
      sentPages.current.add(path);
      pagesViewed.current = [...pagesViewed.current, path];
      sendEvent({
        visitor_cookie_id: id,
        event_type: "page_view",
        page_url: path,
        landing_page: landingPage.current,
        utm_source: utmSource,
        utm_campaign: utmCampaign,
        utm_medium: utmMedium,
        utm_content: utmContent,
        tc: takeTc(),
      });
    }
  }, [consentGranted, utmSource, utmCampaign, utmMedium, utmContent]);


  // Restore personalisation from DB if dynamic mode is active but cache is empty
  useEffect(() => {
    if (!dynamicMode || personalisation) return;
    const id = getCookie(COOKIE_NAME);
    if (!id) return;

    console.log("[Personalisation] Dynamic mode active but no cache — fetching from database");
    
    (async () => {
      try {
        const { data } = await supabase
          .from("visitor_sessions")
          .select("personalisation")
          .eq("visitor_cookie_id", id)
          .maybeSingle();
        
        if (data?.personalisation) {
          console.log("[Personalisation] Restored from database:", Object.keys((data.personalisation as any).pages || {}));
          setPersonalisation(data.personalisation as unknown as PersonalisationDecisions);
          try {
            sessionStorage.setItem(PERSONALISATION_CACHE_KEY, JSON.stringify(data.personalisation));
          } catch { /* ignore */ }
        } else {
          console.log("[Personalisation] No personalisation found in database for this visitor");
        }
      } catch (err) {
        console.warn("[Personalisation] Failed to restore from database:", err);
      }
    })();
  }, [dynamicMode, personalisation]);


  useEffect(() => {
    if (!consentGranted) return;
    const id = visitorId.current || getCookie(COOKIE_NAME);
    if (!id) return;


    const path = location.pathname;
    if (sentPages.current.has(path)) return;
    sentPages.current.add(path);
    pagesViewed.current = [...pagesViewed.current, path];

    sendEvent({
      visitor_cookie_id: id,
      event_type: "page_view",
      page_url: path,
      landing_page: landingPage.current,
      utm_source: utmSource,
      utm_campaign: utmCampaign,
      utm_medium: utmMedium,
      utm_content: utmContent,
      tc: takeTc(),
    });
  }, [location.pathname, utmSource, utmCampaign, utmMedium, utmContent, consentGranted, cookieReady]);

  // Scroll depth tracking
  useEffect(() => {
    if (!consentGranted) return;
    const id = visitorId.current || getCookie(COOKIE_NAME);
    if (!id) return;


    const thresholds = [50, 75, 100];
    const fired = new Set<number>();

    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = Math.round((scrollTop / docHeight) * 100);

      for (const t of thresholds) {
        if (pct >= t && !fired.has(t)) {
          fired.add(t);
          sendEvent({
            visitor_cookie_id: id,
            event_type: `scroll_${t}`,
            page_url: location.pathname,
            metadata: { scroll_pct: t },
          });
        }
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname, consentGranted, cookieReady]);

  const trackEvent = useCallback((type: string, metadata?: Record<string, unknown>) => {
    if (!consentGranted) return;
    const id = visitorId.current || getCookie(COOKIE_NAME);
    if (!id) return;
    sendEvent({
      visitor_cookie_id: id,
      event_type: type,
      page_url: location.pathname,
      metadata,
    });
  }, [location.pathname, consentGranted, cookieReady]);


  const updateProfile = useCallback((fields: Record<string, string>) => {
    // Persist locally for auto-population
    setProfile((prev) => {
      const merged = { ...prev };
      for (const [k, v] of Object.entries(fields)) {
        if (v && v.trim()) {
          (merged as Record<string, string>)[k] = v.trim();
        }
      }
      saveStoredProfile(merged);
      return merged;
    });

    // Send to backend
    if (!hasFunctionalConsent()) return;
    const id = visitorId.current || getCookie(COOKIE_NAME);
    if (!id) return;
    sendEvent({
      visitor_cookie_id: id,
      event_type: "profile_update",
      ...fields,
    });
  }, []);

  /**
   * Ensure a visitor session exists — auto-enables functional consent,
   * creates/reads the visitor cookie, calls track-visitor to upsert a session,
   * and returns the confirmed visitor_cookie_id.
   */
  const ensureVisitorSession = useCallback(async (): Promise<string | null> => {
    // Step 1: auto-enable functional consent. The consent provider must be
    // updated too, not just localStorage — the tracking effects read consent
    // from React state, so writing storage alone leaves page views, scroll
    // depth and chat events silently unrecorded until the next reload.
    if (!hasFunctionalConsent() || (consent && !consent.functional)) {
      enableFunctionalConsent();
      consent?.updatePreferences({ functional: true });
      console.log("[Visitor] Auto-enabled functional consent for Dynamic Mode");
    }

    // Step 2: ensure cookie exists
    let id = getCookie(COOKIE_NAME);
    if (!id) {
      id = generateId();
      setCookie(COOKIE_NAME, id, COOKIE_DAYS);
    }
    visitorId.current = id;
    landingPage.current = landingPage.current || window.location.pathname;

    // Step 3: upsert session via track-visitor
    try {
      const resp = await fetch(EDGE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          visitor_cookie_id: id,
          event_type: "session_init",
          page_url: window.location.pathname,
          landing_page: landingPage.current,
          utm_source: utmSource,
          utm_campaign: utmCampaign,
          utm_medium: utmMedium,
          utm_content: utmContent,
        }),
      });

      if (!resp.ok) {
        console.error("[Visitor] track-visitor failed:", resp.status);
        return null;
      }

      const data = await resp.json();
      console.log("[Visitor] Session ensured, id:", data.session_id || id);
      return id;
    } catch (err) {
      console.error("[Visitor] ensureVisitorSession error:", err);
      return null;
    }
  }, [utmSource, utmCampaign, utmMedium, utmContent, consent]);

  // Trigger personalisation engine — returns structured result
  const triggerPersonalisation = useCallback(async (
    visitor: Record<string, unknown>,
    enrichment?: Record<string, unknown>
  ): Promise<PersonalisationResult> => {
    const id = visitorId.current || getCookie(COOKIE_NAME);
    if (!id) {
      return { ok: false, reason: "no_session" };
    }
    setPersonalisationLoading(true);

    try {
      console.log("[Personalisation] Calling personalise-visit with cookie:", id);
      const resp = await fetch(PERSONALISE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visitor, enrichment, visitor_cookie_id: id }),
      });

      if (!resp.ok) {
        const reason = `http_${resp.status}`;
        const body = await resp.text();
        console.warn("[Personalisation] Failed:", resp.status, body);
        return { ok: false, reason };
      }

      const data = await resp.json();
      console.log("[Personalisation] Response:", JSON.stringify(data).slice(0, 500));
      if (data.personalisation) {
        setPersonalisation(data.personalisation);
        try {
          sessionStorage.setItem(PERSONALISATION_CACHE_KEY, JSON.stringify(data.personalisation));
        } catch { /* storage full — ignore */ }

        // Track the event
        sendEvent({
          visitor_cookie_id: id,
          event_type: "personalisation_applied",
          metadata: {
            has_handoff: !!data.personalisation.handoff_message,
            pages_personalised: Object.keys(data.personalisation.pages || {}),
          },
        });

        return { ok: true, data: data.personalisation };
      }

      console.warn("[Personalisation] No personalisation in response:", data.reason || "unknown");
      return { ok: false, reason: data.reason || "empty_response" };
    } catch (err) {
      console.warn("[Personalisation] Network error:", err);
      return { ok: false, reason: "network_error" };
    } finally {
      setPersonalisationLoading(false);
    }
  }, []);

  return useMemo(() => ({
    visitorCookieId: visitorId.current,
    landingPage: landingPage.current,
    utmSource,
    utmCampaign,
    utmMedium,
    pagesViewed: pagesViewed.current,
    persona,
    setPersona,
    trackEvent,
    updateProfile,
    profile,
    personalisation,
    personalisationLoading,
    triggerPersonalisation,
    dynamicMode,
    setDynamicMode,
    ensureVisitorSession,
    pipelineStatus,
    setPipelineStatus,
    hasPendingTc,
    discardPendingTc,
  }), [utmSource, utmCampaign, utmMedium, persona, setPersona, trackEvent, updateProfile, profile, personalisation, personalisationLoading, triggerPersonalisation, dynamicMode, setDynamicMode, ensureVisitorSession, pipelineStatus, setPipelineStatus, cookieReady, hasPendingTc, discardPendingTc]);
}
